// src/store/supabaseSlice.ts
import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export interface SupabaseProject {
  id: string;
  ref: string;
  name: string;
  organization_id: string;
  organization_slug: string;
  region: string;
  status: string;
}

interface SupabaseState {
  connected: boolean;
  username: string | null;

  // NEW for projects/config
  organizations: any[];
  selectedOrganizationId: string | null;
  projects: SupabaseProject[];
  selectedProjectRef: string | null;     // dropdown selection
  configuredProjectRef: string | null;   // actually configured one
  configuredProjectName: string | null;  // for display
}

const initialState: SupabaseState = {
  connected: false,
  username: null,
  organizations: [],
  selectedOrganizationId: null,
  projects: [],
  selectedProjectRef: null,
  configuredProjectRef: null,
  configuredProjectName: null,
};

const supabaseSlice = createSlice({
  name: "supabase",
  initialState,
  reducers: {
    setSupabaseConnected: (state, action: PayloadAction<boolean>) => {
      state.connected = action.payload;
      if (!action.payload) {
        // if disconnected, clear config
        state.organizations = [];
        state.selectedOrganizationId = null;
        state.projects = [];
        state.selectedProjectRef = null;
        state.configuredProjectRef = null;
        state.configuredProjectName = null;
      }
    },
    setSupabaseUsername: (state, action: PayloadAction<string | null>) => {
      state.username = action.payload;
    },

    // 🔹 store organizations
    setSupabaseOrganizations: (state, action: PayloadAction<any[]>) => {
      state.organizations = action.payload;
    },

    setSupabaseSelectedOrganizationId: (state, action: PayloadAction<string | null>) => {
      state.selectedOrganizationId = action.payload;
    },

    // 🔹 store projects from /projects API
    setSupabaseProjects: (state, action: PayloadAction<SupabaseProject[]>) => {
      state.projects = action.payload;
    },

    // 🔹 currently selected dropdown value
    setSupabaseSelectedProjectRef: (state, action: PayloadAction<string | null>) => {
      state.selectedProjectRef = action.payload;
    },

    // 🔹 when configure API succeeds
    setSupabaseConfiguredProject: (
      state,
      action: PayloadAction<{ ref: string; name: string }>
    ) => {
      state.configuredProjectRef = action.payload.ref;
      state.configuredProjectName = action.payload.name;
      state.selectedProjectRef = action.payload.ref;
    },

    resetSupabase: (state) => {
      state.connected = false;
      state.username = null;
      state.organizations = [];
      state.selectedOrganizationId = null;
      state.projects = [];
      state.selectedProjectRef = null;
      state.configuredProjectRef = null;
      state.configuredProjectName = null;
    },
  },
});

export const {
  setSupabaseConnected,
  setSupabaseUsername,
  setSupabaseOrganizations,
  setSupabaseSelectedOrganizationId,
  setSupabaseProjects,
  setSupabaseSelectedProjectRef,
  setSupabaseConfiguredProject,
  resetSupabase,
} = supabaseSlice.actions;

export default supabaseSlice.reducer;
